import { colors } from '@ardoq/design-tokens';
import { AnimatedChevron, IconSize } from '@ardoq/icons';
import { cloneElement } from 'react';
import {
  InputButton,
  DisplayValueOrPlaceholder,
  ClearIndicatorStyle,
} from './atoms';
import { getSelectButtonStyle } from './getSelectButtonStyle';
import { DEFAULT_ARDOQ_I18NLOCALE, type I18nLocale, t } from '@ardoq/i18n';

type SelectComponentButtonProps = {
  /**
   * Optional custom button element to trigger the dropdown.
   * When provided, replaces the default select button.
   * The button will receive onClick, ref, and style props automatically.
   */
  selectButton?: React.ReactElement<{
    onClick?: React.MouseEventHandler;
    ref?: React.Ref<HTMLElement> | undefined;
    style?: React.CSSProperties;
  }>;
  onClick: React.MouseEventHandler;
  hostRef: React.Ref<HTMLElement>;
  isOpen: boolean;
  defaultValue?: string;
  placeholderText: string;
  hasClearButton: boolean;
  refClearButton: React.Ref<HTMLDivElement>;
  hasSearchInputInDropdown: boolean;
  i18nLocale?: I18nLocale;
};

export const SelectComponentButton = ({
  selectButton,
  onClick,
  hostRef,
  isOpen,
  defaultValue,
  placeholderText,
  hasClearButton,
  refClearButton,
  hasSearchInputInDropdown,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
}: SelectComponentButtonProps) => {
  if (selectButton) {
    return cloneElement(selectButton, {
      onClick,
      ref: hostRef,
      style: getSelectButtonStyle(hasSearchInputInDropdown, isOpen),
    });
  }

  return (
    <InputButton
      ref={hostRef as React.RefObject<HTMLButtonElement>}
      onClick={onClick}
      $alignText="left"
    >
      <DisplayValueOrPlaceholder
        $isPlaceholder={!defaultValue}
        $isOpen={isOpen}
      >
        {defaultValue || placeholderText}
      </DisplayValueOrPlaceholder>
      {hasClearButton && defaultValue && (
        <ClearIndicatorStyle
          $isVisible={!isOpen}
          ref={refClearButton}
          role="button"
          data-test-id={'clear-all'}
          aria-label={t(
            i18nLocale,
            'dropdownMenu:clearValueAriaLabel',
            'Clear value'
          )}
        >
          {t(i18nLocale, 'dropdownMenu:clear', 'Clear')}
        </ClearIndicatorStyle>
      )}
      <AnimatedChevron
        $isUp={isOpen}
        iconSize={IconSize.MEDIUM}
        color={colors.iconAction}
      />
    </InputButton>
  );
};
